import React from "react";
import {
  usePassionsData,
  POPULAR_PASSION_IDS,
  PASSION_DEFINITIONS,
} from "~/hooks/usePassionsData";
import { usePassionsStore } from "~/store";
import { Carousel } from "../Carousel";
import { ContentCard } from "../ContentCard";
import { getAnalyticsClickTrackingAttributes } from "~/utils/adobeDataLayerUtils";

const hobbies = [
  {
    passion: "golf",
    title: "Golf Vacation Rentals for Golf Enthusiasts",
    description:
      "Tee off in style with golf vacation home rentals near top-tier courses. From private putting greens to in-home golf carts, these curated stays are nestled in elite golf communities with access to member-only courses, academies, and driving ranges. Whether you're chasing birdies or relaxing post-round, these golf home rentals blend luxury and fairway living for the ultimate golfer's getaway.",
    imgSrc:
      "https://homes-and-villas.marriott.com/assets/images/collections/Golf_Vacation_Rentals_3x2.png?imwidth=429",
    url: "https://homes-and-villas.marriott.com/en/collections/golf-home-rentals",
  },
  {
    passion: "ski",
    title: "Ski-In, Ski-Out Chalets Curated for Slope-Seekers",
    description:
      "Gear up for the ultimate Ski-In/Ski-Out experience: Wake up and carve the first tracks with our exclusive collection of ski-in/ski-out vacation homes. Discover our inviting ski house rentals, paired with the unmatched convenience of being right on the slopes. Cozy up beside the crackling fireplace in a warm Vail hideaway, or retreat to an Alpine chalet in southwestern Switzerland.",
    imgSrc:
      "https://homes-and-villas.marriott.com/assets/images/collections/Ski%202.jpeg?imwidth=429",
    url: "https://homes-and-villas.marriott.com/en/collections/ski-in-ski-out-homes",
  },
  {
    passion: "beach",
    title: "Beach Front Rentals Curated for Sea Lovers",
    description:
      "Soak up the sun and sink your toes into sandy beaches with our curated beach rentals. Whether it's a weekend surf trip or a snorkeling-filled family reunion, our oceanfront vacation rentals offer the perfect blend of comfort and adventure. Choose from a number of coastal escapes in global destinations for your next ocean rental getaway.",
    imgSrc:
      "https://homes-and-villas.marriott.com/assets/images/collections/beachfront_hero_3x2.jpg?imwidth=429",
    url: "https://homes-and-villas.marriott.com/en/collections/oceanfront-homes",
  },
  {
    passion: "spa",
    title: "Resort Inspired Homes for Comfort Seekers",
    description:
      "Discover home rentals with resort-style amenities perfect for extended stays or family getaways. Enjoy access to shared sport courts, golf courses, private chefs, villa attendants, and daily housekeeping. From waterslides to on-site family activities, these vacation rentals offer the luxury of a resort with the comfort of a private home.",
    imgSrc:
      "https://homes-and-villas.marriott.com/assets/images/collections/resort_collection_3x2.jpg?imwidth=429",
    url: "https://homes-and-villas.marriott.com/en/collections/resort-amenities-homes",
  },
  {
    passion: "culinary",
    title: "Homes with Top Kitchens for the Chefs",
    description:
      "Cook, celebrate, and connect in vacation rentals with chef-ready kitchens. From a Tuscan villa perfect for family feasts to a Lake Tahoe retreat with gourmet appliances or a Scottsdale home with open-concept dining, these rentals are made for memorable meals.\n" +
      "Whether you're cooking together or hiring a private chef, these kitchens turn every stay into a culinary experience.",
    imgSrc:
      "https://homes-and-villas.marriott.com/assets/images/collections/amazing_kitchen_3x2.jpg?imwidth=429",
    url: "https://homes-and-villas.marriott.com/en/collections/gourmet-kitchen-homes",
  },
  {
    passion: "culture",
    title: "Cool and Unique Rentals for Trend Setters",
    description:
      "Unforgettable stays in top destinations. Explore hand-picked, one-of-a-kind vacation rentals like a reimagined 1800s water tower in Southern California, a peaceful yurt on the Oregon Coast, or a restored 1916 CSX caboose in Pigeon Forge. These unique home rentals deliver truly distinctive experiences to make each stay memorable.",
    imgSrc:
      "https://homes-and-villas.marriott.com/assets/images/collections/unique_hero.jpg?imwidth=429",
    url: "https://homes-and-villas.marriott.com/en/collections/eclectic-escapes",
  },
  {
    passion: "culture",
    title: "Rentals near Top Events for Spirited Fans",
    description:
      "Stay close to the action with vacation rentals near global sporting celebrations. From high-stakes matches and fan festivals to cultural showcases and city-wide gatherings, these curated homes offer comfort and convenience. Whether it's the thrill of international competition or the energy of a packed stadium, enjoy a front-row experience with a vacation rental that keeps you near the excitement of a world soccer game.",
    imgSrc:
      "https://homes-and-villas.marriott.com/assets/images/collections/Global_Events_3x2.png?imwidth=429",
    url: "https://homes-and-villas.marriott.com/en/collections/global-events",
  },
];

const EXPLORE_HOMES_URL =
  "https://homes-and-villas.marriott.com/en/collections";

export const HobbyCarousel = () => {
  const { getSelectedPassions } = usePassionsData();
  const storeSelectedPassions = usePassionsStore(
    (state) => state.selectedPassions,
  );

  const cdpPassions = getSelectedPassions();
  const selectedPassions =
    storeSelectedPassions && storeSelectedPassions.length > 0
      ? storeSelectedPassions
      : cdpPassions;

  const getSlides = () => {
    let activePassions = selectedPassions || [];

    // Fallback to popular if no passions selected
    if (activePassions.length === 0) {
      activePassions = POPULAR_PASSION_IDS;
    }

    if (activePassions.length > 1) {
      // If more than one passion, show 1 tile for each
      return activePassions
        .map((passion) => hobbies.find((h) => h.passion === passion))
        .filter(Boolean);
    }

    // If only one passion (or none but fallback was triggered above and somehow logic flowed here,
    // though length > 1 covers popular since len=3), show all tiles for that passion
    return hobbies.filter((h) => h.passion === activePassions[0]);
  };

  const slides = getSlides();

  if (!slides || slides.length === 0) {
    return null;
  }

  const handleSlideClick = (slide) => {
    // Open the button link in a new tab
    if (slide.url) {
      window.open(slide.url, "_blank", "noopener,noreferrer");
    }
  };

  const handleTitleClick = () => {
    window.open(EXPLORE_HOMES_URL, "_blank", "noopener,noreferrer");
  };

  const isSingleSlide = slides.length === 1;

  return (
    <div data-testid="homes-villas-carousel">
      <Carousel
        title={"Explore Homes & Villas"}
        gap={32}
        navButtonTop="220px"
        onTitleClick={handleTitleClick}
      >
        {slides.map((slide, index) => {
          const cardStyle = isSingleSlide
            ? { width: "830px", maxWidth: "calc(100vw - 48px)" }
            : { width: "280px" };

          const imageStyle = isSingleSlide
            ? { height: "440px", width: "100%", objectFit: "cover" }
            : { height: "320px", width: "100%", objectFit: "cover" };

          // Use higher resolution image for the featured single card
          let imageSrc = slide.imgSrc;
          if (isSingleSlide && imageSrc) {
            imageSrc = imageSrc.replace(/imwidth=\d+/, "imwidth=1660");
          }

          const passionDef = PASSION_DEFINITIONS[slide.passion];
          const activities =
            passionDef && passionDef.icon
              ? [
                  {
                    label: passionDef.label,
                    icon: passionDef.icon,
                  },
                ]
              : [];

          return (
            <ContentCard
              key={`${slide.passion}-${index}`}
              title={slide.title}
              description={slide.description}
              image={imageSrc}
              onClick={() => handleSlideClick(slide)}
              style={cardStyle}
              imageStyle={imageStyle}
              activities={activities}
              {...getAnalyticsClickTrackingAttributes(
                "Hobby Carousel",
                "click",
                `Opened ${slide.title}`,
              )}
            />
          );
        })}
      </Carousel>
    </div>
  );
};
